/**
 * <ModuleGate module="budgets">...</ModuleGate>
 *
 * Two modes:
 *   - mode="block" (default): renders a soft-block banner explaining the module
 *     is not enabled for the company's plan. Children are NOT rendered.
 *     Best for full pages/routes.
 *   - mode="hide": renders nothing if the module is disabled.
 *     Best for sidebar items / inline buttons.
 *
 * Usage:
 *   <ModuleGate module="budgets"><BudgetsPage /></ModuleGate>
 *   <ModuleGate module="contracts" mode="hide"><MenuLink ... /></ModuleGate>
 */
import { Lock, Mail } from 'lucide-react';
import useModules from './useModules';
import { getModule } from './catalog';

export default function ModuleGate({ module, mode = 'block', fallback, children }) {
  const { isEnabled } = useModules();
  if (isEnabled(module)) return children;

  if (mode === 'hide') return null;
  if (fallback !== undefined) return fallback;

  const meta = getModule(module);

  return (
    <div className="max-w-2xl mx-auto my-12 p-8 bg-white border border-gray-200 rounded-2xl shadow-sm text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-5">
        <Lock className="w-8 h-8" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        {meta.name} module is not enabled
      </h2>
      <p className="text-sm text-gray-600 mb-1">
        This feature is part of the <span className="font-medium">{meta.icon} {meta.name}</span> module,
        which is not currently included in your plan.
      </p>
      {meta.description && (
        <p className="text-xs text-gray-500 mb-5">{meta.description}</p>
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800 inline-flex items-center gap-2">
        <Mail className="w-4 h-4" />
        Contact your administrator to enable this module.
      </div>
    </div>
  );
}
